import { useCallback, useEffect, useRef, useState } from 'react';
import client from '../../lib/api';
import {
  BULK_DETAIL_MAX,
  BULK_DETAIL_PROVIDERS,
  hostedDetailPath,
  hostedDetailsPath,
} from './hostedBets';

// Hazır spor kitabı kuponlarının içini (maçlar, seçimler, oranlar) listeye
// doldurur.
//
// Kuponun içi para hareketiyle gelmez ve sağlayıcının kupon bildirimi isteğe
// bağlıdır; kurulmamışsa gövde yerelde hiç oluşmaz ve liste yalnızca tutar
// gösterirdi. Bu yüzden eksik olanlar açılışta sağlayıcıdan istenir ve
// satırlar doldukça güncellenir; uç gelen gövdeyi saklar, yani bu bedel kupon
// başına bir kez ödenir ve sonraki açılışlar anında dolu gelir.
//
// **Sağlayıcı toplu okuma veriyorsa sayfa tek istekle dolar.** Flexrix'in
// ``GET /v1/sportsbook-bets`` ucu tek çağrıda 50 kimlik alıyor; buna rağmen
// kupon başına bir istek atılıyordu ve 25 satırlık bir sayfa, sağlayıcının
// kenar katmanı (Cloudflare) için saniyede dört istekli bir taramaya
// benziyordu. Toplu uç olmayan sağlayıcıda (SynCode) eski sıra korunur.
//
// Üç kural:
//
// * **Toplu istek sunucuda tur atmaz.** Uç ya sağlayıcının kendi toplu ucunu
//   çağırır ya da yalnızca yereldekileri döndürür; sunucuda kupon kupon
//   dolaşmak isteği sağlayıcının gecikmesine bağlar ve tek worker'lı
//   backend'de dakikalarca açık tutardı.
// * **Aralık sağlayıcıya göredir.** SynCode'un rapor uçları saniyede bir
//   istekle sınırlıdır (bkz. CLAUDE.md); art arda atmak 429 üretir.
// * **Aynı kupon iki kez denenmez.** Başarısız olan satır işaretlenir, aksi
//   hâlde efekt her güncellemede onu yeniden sorup döngüye girerdi; yönetici
//   isterse satırdaki düğmeyle elle tekrar dener. Toplu istekte sağlayıcının
//   tanımadığı kimlikler de (``missing``) işaretlenir: sağlayıcıda karşılığı
//   olmayan kupon her listelemede yeniden sorulurdu.

const GAP_MS = { syncode: 1200, flexrix: 250 };

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export const useHostedBetDetails = (items, setItems) => {
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const attempted = useRef(new Set());
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const applyDetail = useCallback((id, data) => {
    setItems((rows) => rows.map((row) => (row.id === id ? {
      ...row,
      selections: data.selections || [],
      odds: Number(row.odds) > 0 ? row.odds : (data.odds || 0),
      detail: data.detail,
      has_detail: true,
      detail_unparsed: !(data.selections || []).length,
    } : row)));
  }, [setItems]);

  const fetchOne = useCallback(async (item) => {
    attempted.current.add(item.id);
    setBusy((current) => ({ ...current, [item.id]: true }));
    setErrors((current) => {
      if (!current[item.id]) return current;
      const next = { ...current };
      delete next[item.id];
      return next;
    });
    try {
      const { data } = await client.get(hostedDetailPath(item));
      applyDetail(item.id, data);
      return true;
    } catch (error) {
      setErrors((current) => ({
        ...current,
        [item.id]: error.response?.data?.detail || 'Kupon içeriği alınamadı',
      }));
      return false;
    } finally {
      setBusy((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
    }
  }, [applyDetail]);

  // Toplu okuma: bir sağlayıcının eksik kuponları tek istekte istenir.
  // Sağlayıcının tanımadığı kimlik hata değildir ama **işaretlenir**, yoksa
  // her listelemede yeniden sorulurdu.
  const fetchMany = useCallback(async (provider, rows) => {
    rows.forEach((row) => attempted.current.add(row.id));
    setBusy((current) => {
      const next = { ...current };
      rows.forEach((row) => { next[row.id] = true; });
      return next;
    });
    try {
      const betIds = rows.map((row) => row.bet_id);
      const { data } = await client.get(hostedDetailsPath(provider, betIds));
      const items = data.items || {};
      rows.forEach((row) => {
        const found = items[row.bet_id];
        if (found) applyDetail(row.id, found);
      });
      // Sunucu toplu ucu desteklemiyorsa yalnızca yereldekiler gelmiştir;
      // kalanlar tek tek istenmelidir (istemci ile sunucunun sağlayıcı
      // listesi ayrışırsa sayfa sessizce boş kalırdı).
      if (!data.bulk) {
        const rest = rows.filter((row) => !items[row.bet_id]);
        rest.forEach((row) => attempted.current.delete(row.id));
        return rest;
      }
    } catch (error) {
      const message = (
        error.response?.data?.detail || 'Kupon içeriği alınamadı'
      );
      setErrors((current) => {
        const next = { ...current };
        rows.forEach((row) => { next[row.id] = message; });
        return next;
      });
    } finally {
      setBusy((current) => {
        const next = { ...current };
        rows.forEach((row) => { delete next[row.id]; });
        return next;
      });
    }
    return [];
  }, [applyDetail]);

  // Efekt sayfadaki kimliklere bağlanır, satırların kendisine değil: içerik
  // yazıldığında ``items`` değişir ve nesneye bağlı bir efekt her seferinde
  // baştan başlayıp sırayı bozardı.
  const signature = items.map((item) => item.id).join('|');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Liste sitenin kendi kuponlarıyla karışıktır; onların içeriği zaten
      // kupon satırındadır ve sağlayıcıya sorulacak bir şey yoktur.
      const pending = itemsRef.current.filter((item) => (
        item.source === 'hosted'
        && !item.has_detail
        && !attempted.current.has(item.id)
      ));

      const bulk = pending.filter(
        (item) => BULK_DETAIL_PROVIDERS.has(item.provider),
      );
      const byProvider = new Map();
      bulk.forEach((item) => {
        const rows = byProvider.get(item.provider) || [];
        rows.push(item);
        byProvider.set(item.provider, rows);
      });
      // Toplu ucu olmayan sağlayıcının satırları ve toplu istekten geri kalan
      // satırlar aynı sıraya düşer.
      const queue = pending.filter(
        (item) => !BULK_DETAIL_PROVIDERS.has(item.provider),
      );
      for (const [provider, rows] of byProvider) {
        for (let i = 0; i < rows.length; i += BULK_DETAIL_MAX) {
          if (cancelled) return;
          const rest = await fetchMany(
            provider, rows.slice(i, i + BULK_DETAIL_MAX));
          queue.push(...rest);
        }
      }

      for (const item of queue) {
        if (cancelled) return;
        await fetchOne(item);
        if (cancelled) return;
        await wait(GAP_MS[item.provider] ?? 400);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, fetchOne, fetchMany]);

  const retry = useCallback((item) => {
    attempted.current.delete(item.id);
    return fetchOne(item);
  }, [fetchOne]);

  return { busy, errors, retry };
};

export default useHostedBetDetails;
